<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { XIcon, CheckIcon, CircleCheckIcon, AlertCircleIcon } from 'vue-tabler-icons';
import { companySettingsApi, type PlanData } from '../api/companySettingsApi';
import visaIcon from '@/assets/images/svgs/visa.svg';
import mastercardIcon from '@/assets/images/svgs/mastercard.svg';
import amexIcon from '@/assets/images/svgs/amex.svg';
import jcbIcon from '@/assets/images/svgs/jcb.svg';
import unionpayIcon from '@/assets/images/svgs/unionpay.svg';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { loadAirwallex, createElement, confirmPaymentIntent } from 'airwallex-payment-elements';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  modelValue: boolean;
  currentPlan: string;
  baseEntities: number;
  existingAdditional: number;
  entityUsage: number;
  trialDaysRemaining?: number;
  renewalMode?: boolean;
  billingPeriod?: string;
  billingEmail?: string;
  billingContactName?: string;
  requiredEntities?: number;
  prorationRemainingDays?: number | null; // 증설 프로레이션: 잔여일
  prorationTotalDays?: number | null;     // 증설 프로레이션: 총 기간일
  subscriptionEndDate?: string | null;    // 현재 구독 종료일(갱신 후 만료일 미리보기용)
}>();

const sessionStore = useSessionStore();
const customerInfo = computed(() => sessionStore.getCustomerInfo());

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'planUpdated', payload: { plan: string; duration: string; addEntities: number; amount: number; isMonthly: boolean }): void;
}>();

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

/* Snackbar (component-level) */
const snackbar = ref({ show: false, message: '', color: 'success' as string });
function showSnackbar(message: string, color: 'success' | 'error' | 'warning' | 'info' = 'success') {
  snackbar.value = { show: true, message, color };
}

/* ???? Step Control ???? */
// step: 1=Plan Selection, 2=Payment, 3=Result
const step = ref(1);
const paymentStatus = ref<'idle' | 'processing' | 'success' | 'failed'>('idle');
const paymentMessage = ref('');

function periodToDuration(period?: string): string {
  const map: Record<string, string> = {
    monthly: 'monthly', yearly: '1y', '3yearly': '3y', '5yearly': '5y'
  };
  if (map[period || '']) return map[period || ''];
  // custom period: e.g. '6yearly' ??'custom'
  const m = (period || '').match(/^(\d+)yearly$/);
  if (m) return 'custom';
  return '1y';
}

function periodToCustomYears(period?: string): number {
  const m = (period || '').match(/^(\d+)yearly$/);
  return m ? parseInt(m[1], 10) : 1;
}

watch(dialogVisible, (val) => {
  if (val) {
    step.value = 1;
    paymentStatus.value = 'idle';
    paymentMessage.value = '';
    appliedPromo.value = null;
    promoSkipped.value = false;
    manualPromoInput.value = '';
    manualPromoResult.value = null;
    manualPromoExpanded.value = false;
    if (props.renewalMode) {
      selectedPlan.value = props.currentPlan;
      const dur = periodToDuration(props.billingPeriod);
      selectedDuration.value = dur;
      if (dur === 'custom') {
        customYears.value = periodToCustomYears(props.billingPeriod);
      }
      entityLimitInput.value = props.baseEntities;
      entityCount.value = props.baseEntities;
    } else {
      selectedPlan.value = props.currentPlan === 'trial' ? 'enterprise' : props.currentPlan;
      // ?? ???? ????? ????? billing period ????, Trial: ?? 1y
      if (props.currentPlan !== 'trial' && props.billingPeriod) {
        const dur = periodToDuration(props.billingPeriod);
        selectedDuration.value = dur;
        if (dur === 'custom') {
          customYears.value = periodToCustomYears(props.billingPeriod);
        }
      } else {
        selectedDuration.value = '1y';
      }
    }
    airwallexError.value = '';
    destroyAirwallexElement();
    // Step 1 ????? ??SDK ???? ????? ??Step 2 ????? ??????mount
    preloadAirwallexSdk();
    if (paidPlans.value.length === 0) {
      loadPlans();
    }
    // 프로모션 자동적용(Type A)을 Step 1(이전 화면)·견적서에도 반영하기 위해 다이얼로그 오픈 시점에 조회.
    // (기존엔 Step 2 진입 시에만 조회 → 이전 화면/견적서엔 정가만 노출됐음)
    // 만료/사용불가 프로모는 서버 getPromoAvailability가 걸러 반환하지 않으므로 자동으로 미표시된다.
    fetchApplicablePromo();
  }
});

/* ???? Plan Selection ???? */
const selectedPlan = ref('starter');
const selectedDuration = ref<string>('1y');
const customYears = ref(6);
const currencySymbolMap: Record<string, string> = { SGD: 'S$', MYR: 'RM', USD: 'US$', EUR: '?', GBP: '?' };
const currencySymbol = computed(() => currencySymbolMap[planCurrency.value] || planCurrency.value);

const isTrial = computed(() => props.currentPlan === 'trial');
const trialDisabled = computed(() => !isTrial.value);
const isPlanChanged = computed(() => selectedPlan.value !== props.currentPlan);
// ????(ENTITY_ADDON): ?? ???? ???????? ????? ??????? ????????? ???? ??period ????
const isPeriodLocked = computed(() => !isTrial.value && !props.renewalMode && !isPlanChanged.value);

interface PlanDef {
  key: string;
  name: string;
  planCd: string;
  minEntityFirst: number;
  minEntityAddon: number;
  prices: Record<string, number>;
  taxRate: number;
  taxLabel: string | null;
}

const paidPlans = ref<PlanDef[]>([]);
const plansLoading = ref(false);
const planCurrency = ref('SGD');

async function loadPlans() {
  plansLoading.value = true;
  try {
    const res = await companySettingsApi.getPlans();
    const plans = res?.data?.plans || [];
    paidPlans.value = plans
      .filter((p: PlanData) => !p.isTrial)
      .map((p: PlanData) => ({
        key: p.planKey,
        name: p.planName,
        planCd: p.planCd,
        minEntityFirst: p.minEntityFirst || 30,
        minEntityAddon: p.minEntityAddon || 10,
        prices: {
          monthly: p.pricing?.priceMonthly || 0,
          '1y': p.pricing?.priceYearly || 0,
          '3y': p.pricing?.price3Yearly || (p.pricing?.priceYearly || 0) * 0.9,
          '5y': p.pricing?.price5Yearly || (p.pricing?.priceYearly || 0) * 0.8
        },
        taxRate: p.pricing?.taxRate || 0,
        taxLabel: p.pricing?.taxLabel || null
      }));
    const pricedPlan = plans.find((p: PlanData) => p.pricing?.currency);
    if (pricedPlan) {
      planCurrency.value = pricedPlan.pricing!.currency;
    }
  } catch (err: any) {
    console.error('Failed to load plans:', err);
    paidPlans.value = [];
  } finally {
    plansLoading.value = false;
  }
}

const isMonthly = computed(() => selectedDuration.value === 'monthly');
const isCustomDuration = computed(() => selectedDuration.value === 'custom');
const effectiveYears = computed(() => {
  if (isCustomDuration.value) return Math.max(6, customYears.value || 6);
  const map: Record<string, number> = { '1y': 1, '3y': 3, '5y': 5 };
  return map[selectedDuration.value] || 1;
});

function onCustomYearsBlur() {
  customYears.value = Math.max(6, Math.min(10, customYears.value || 6));
}

const selectedPlanDef = computed(() => paidPlans.value.find(p => p.key === selectedPlan.value));

const threeYearLabel = computed(() => {
  const plan = selectedPlanDef.value;
  if (plan && plan.prices['1y'] > 0 && plan.prices['3y'] < plan.prices['1y']) {
    const pct = Math.round((1 - plan.prices['3y'] / plan.prices['1y']) * 100);
    return t('subscription.planDialog.durationSavedYears', { years: 3, pct });
  }
  return t('subscription.planDialog.durationYears', { years: 3 });
});

const fiveYearLabel = computed(() => {
  const plan = selectedPlanDef.value;
  if (plan && plan.prices['1y'] > 0 && plan.prices['5y'] < plan.prices['1y']) {
    const pct = Math.round((1 - plan.prices['5y'] / plan.prices['1y']) * 100);
    return t('subscription.planDialog.durationSavedYears', { years: 5, pct });
  }
  return t('subscription.planDialog.durationYears', { years: 5 });
});
const selectedPlanPrice = computed(() => {
  if (isCustomDuration.value) {
    // custom ??? (6??)?? 5y ?????? ???
    return selectedPlanDef.value?.prices['5y'] ?? 0;
  }
  return selectedPlanDef.value?.prices[selectedDuration.value] ?? 0;
});

/* ???? Entity Count ???? */

const entityCount = ref(30);
/* ???? Entity Limit ???? */
const includedBase = computed(() => selectedPlanDef.value?.minEntityFirst || 30);
const entityStep = computed(() => selectedPlanDef.value?.minEntityAddon || 10);

// 10????? ???
function ceilToStep(n: number, step: number): number {
  return Math.ceil(n / step) * step;
}

// Warn when below active entity count
const minRequired = computed(() => {
  return Math.max(includedBase.value, ceilToStep(props.entityUsage, entityStep.value));
});

// ??? ???????? ??????
const entityLimitInput = ref(30);

// entityCount를 billing 계산용으로 동기화 + 최솟값 보정
watch(entityLimitInput, (val) => {
  if (selectedPlan.value === 'trial') {
    entityCount.value = 0;
    return;
  }
  if (val < minRequired.value) {
    entityLimitInput.value = minRequired.value;
    return; // 재귀 트리거로 다시 진입
  }
  entityCount.value = val || 0;
});

// blur 시 10단위 올림 + active count 이상 보정
function onEntityLimitBlur() {
  if (selectedPlan.value === 'trial') return;
  let v = entityLimitInput.value || 0;
  v = ceilToStep(Math.max(minRequired.value, v), entityStep.value);
  entityLimitInput.value = v;
}

const entityLimitQuickDisabled = computed(
  () => selectedPlan.value === 'trial' || props.renewalMode
);

/** Entity Limit quick increment (+10 / +100 / +1000) */
function addEntityLimitQuick(delta: number) {
  if (entityLimitQuickDisabled.value) return;
  let v = (entityLimitInput.value || 0) + delta;
  v = ceilToStep(Math.max(includedBase.value, v), entityStep.value);
  entityLimitInput.value = v;
}

// Warn when below active entity count
const entityBelowActive = computed(() => {
  return selectedPlan.value !== 'trial' && entityLimitInput.value < props.entityUsage;
});

// ?????????? (????????????????????: currentPlan??trial??? baseEntities ??? ??????? ??????
const isFirstSubscription = computed(() => props.currentPlan === 'trial' || props.baseEntities <= 0);

// ?????????: ?????(Included 30) | ?????(?? ??????? | ?????(???? ??????
const barTotal = computed(() => Math.max(1, entityLimitInput.value));

// ?????: ????? ?? ????? (???? 30)
const barIncluded = computed(() => Math.min(includedBase.value, entityLimitInput.value));

// ?????: ?? ?????????base ?????(?????????0)
const barExisting = computed(() => {
  if (isFirstSubscription.value) return 0;
  const existingBeyondBase = Math.max(0, props.baseEntities - includedBase.value);
  const remainingAfterBase = Math.max(0, entityLimitInput.value - barIncluded.value);
  return Math.min(existingBeyondBase, remainingAfterBase);
});

// ?????: ???? ??????(??????base ????? ??=????? ????? ?????
const barNew = computed(() => {
  if (isFirstSubscription.value) {
    return Math.max(0, entityLimitInput.value - barIncluded.value);
  }
  return Math.max(0, entityLimitInput.value - props.baseEntities);
});

const includedBarPct = computed(() => (barIncluded.value / barTotal.value) * 100);
const existingBarPct = computed(() => (barExisting.value / barTotal.value) * 100);
const newBarPct = computed(() => (barNew.value / barTotal.value) * 100);

watch(selectedPlan, (val) => {
  if (val === 'trial') {
    entityLimitInput.value = 0;
    entityCount.value = 0;
    selectedDuration.value = '1y';
  } else {
    // ????? ????? ??Active ??? ????????? ????
    entityLimitInput.value = minRequired.value;
  }
});

watch(dialogVisible, (val) => {
  if (val) {
    if (props.requiredEntities && props.requiredEntities > 0) {
      // limit ???????? ????: ???????????????????????? ????
      entityLimitInput.value = ceilToStep(Math.max(minRequired.value, props.requiredEntities), entityStep.value);
    } else if (props.currentPlan !== 'trial' && props.baseEntities > 0) {
      // ????: ????? ???? ????
      entityLimitInput.value = Math.max(props.baseEntities, minRequired.value);
    } else {
      // Trial??Enterprise: active ?????????? ???????
      entityLimitInput.value = minRequired.value;
    }
    entityCount.value = entityLimitInput.value;
  }
}, { flush: 'post' });

// ?? ????????? (?????=????, ?????=?????, 0=???)
const entityDelta = computed(() => entityCount.value - props.baseEntities);
const addEntities = computed(() => Math.max(0, entityDelta.value));

// ??? ????????
const isAddonMode = computed(() => !isPlanChanged.value && !props.renewalMode && entityDelta.value > 0);

// 감소 모드: 같은 플랜에서 limit만 낮춤 — 결제 없음, 다음 갱신부터 감소 금액 반영
const isDecreaseMode = computed(() =>
  !isPlanChanged.value && !props.renewalMode && selectedPlan.value !== 'trial' && entityDelta.value < 0
);

// 결제 유형 — buildPaymentPayload와 프로모 조회(§5.2-0)가 동일 기준을 공유해야 하므로 computed로 분리.
const paymentType = computed(() => props.renewalMode
  ? 'RENEWAL'
  : isPlanChanged.value ? 'PLAN_NEW' : (addEntities.value > 0 ? 'ENTITY_ADDON' : 'PLAN_NEW'));

const managedDisplay = computed(() => {
  if (isTrial.value) return `${props.entityUsage} / ${includedBase.value}`;
  return `${props.entityUsage} / ${props.baseEntities}`;
});

/* ???? Billing Summary ???? */
// ENTITY_ADDON: ???????? ???, PLAN_NEW/RENEWAL: ???
// 청구 대상 엔티티 수:
//  - 갱신/플랜변경 → 전체 entityCount (신규/갱신 구매)
//  - 증설(같은 플랜, delta>0) → 증가분만
//  - 변경 없음(delta=0) → 0 (청구 없음). 기존엔 전체로 빠져 이미 가입 상태인데 전체금액이 나오던 버그.
const billingEntityCount = computed(() => {
  if (props.renewalMode || isPlanChanged.value) return entityCount.value;
  if (entityDelta.value > 0) return entityDelta.value;
  return 0;
});

// 증설 프로레이션 비율: 현재 구독의 잔여일/총일수. 증설(add-on)만 적용, 신규/갱신/월간전환은 전체(=1).
// 데이터 없으면 1(전체)로 안전 폴백.
// 증설(add-on)은 **잔여기간(일) 비례**로 청구한다: 금액 = 연단가 × 수량 × (잔여일 / 365).
// 갱신 stacking(B안)으로 구독 창이 한 텀과 달라도(예: 잔여 60일 + N년) 남은 기간만큼만 부과 → 백엔드(payment.js)와 일치.
// 잔여일 데이터가 없으면 effectiveYears(전체 기간)로 폴백.
const addonRemainingDays = computed<number | null>(() => {
  const r = props.prorationRemainingDays;
  return r != null && r > 0 ? r : null;
});
const addonYears = computed(() => {
  if (!isAddonMode.value) return effectiveYears.value;
  return addonRemainingDays.value != null ? addonRemainingDays.value / 365 : effectiveYears.value;
});
const isProrated = computed(() => isAddonMode.value && addonRemainingDays.value != null);

// ── 갱신 후 만료일 미리보기 ──
// 백엔드 provisioning(payment.js)과 동일: 기존 종료일이 미래면 그 종료일부터, 아니면 오늘부터 기간을 더한다.
// DB DATETIME(공백 직렬화, TZ 없음)을 UTC로 정규화해 파싱(로컬 파싱 시 하루 밀림 방지).
function parseDbDate(v?: string | null): Date | null {
  if (!v) return null;
  let s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) s = s.replace(' ', 'T');
  if (s.includes('T') && !/(Z|[+-]\d{2}:?\d{2})$/.test(s)) s += 'Z';
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
function fmtYmd(d: Date | null): string {
  if (!d) return '-';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}/${m}/${day}`;
}
const currentExpiryDate = computed(() => parseDbDate(props.subscriptionEndDate));
const renewalNewExpiry = computed<Date | null>(() => {
  if (!props.renewalMode) return null;
  const now = new Date();
  const cur = currentExpiryDate.value;
  // 기존 종료일이 미래면 이어붙임(stack), 지났으면(만료) 오늘부터.
  const base = cur && cur.getTime() > now.getTime() ? new Date(cur.getTime()) : new Date(now.getTime());
  if (isMonthly.value) base.setUTCMonth(base.getUTCMonth() + 1);
  else base.setUTCFullYear(base.getUTCFullYear() + effectiveYears.value);
  return base;
});
const currentExpiryLabel = computed(() => fmtYmd(currentExpiryDate.value));
const newExpiryLabel = computed(() => fmtYmd(renewalNewExpiry.value));
// 기존 만료일이 이미 지났으면 "이어붙임"이 아니라 오늘부터 시작임을 안내
const renewalStacksFromExpiry = computed(() => {
  const cur = currentExpiryDate.value;
  return !!(cur && cur.getTime() > Date.now());
});

const totalCost = computed(() => {
  if (selectedPlan.value === 'trial') return 0;
  if (billingEntityCount.value <= 0) return 0;
  if (isMonthly.value) return selectedPlanPrice.value * billingEntityCount.value;
  // 증설: 연단가 × 수량 × 잔여연수(잔여일/365). 그 외(신규/갱신): 전체 기간(연단가 × 수량 × 연수).
  if (isAddonMode.value) {
    return Math.round(selectedPlanPrice.value * billingEntityCount.value * addonYears.value);
  }
  return selectedPlanPrice.value * billingEntityCount.value * effectiveYears.value;
});

const taxRate = computed(() => {
  const plan = paidPlans.value.find(p => p.key === selectedPlan.value);
  return plan?.taxRate || 0;
});

const taxLabel = computed(() => {
  const plan = paidPlans.value.find(p => p.key === selectedPlan.value);
  return plan?.taxLabel || t('subscription.planDialog.tax');
});

const taxAmount = computed(() => {
  if (taxRate.value <= 0) return 0;
  return Math.round(totalCost.value * taxRate.value * 100) / 100;
});

const grandTotal = computed(() => totalCost.value + taxAmount.value);

/* ──── 프로모션 코드(§5.2) ──── */
// Type A(잠금) 자동 적용 — 서버가 이 회사에 연결된 usable 코드를 찾아준다. null이면 없음.
const appliedPromo = ref<{ code: string; discountRate: number; phase: 'FIRST' | 'REUSE'; deadline: string | null } | null>(null);
// 사용자가 자동적용 칩을 "적용 안 함"으로 끈 경우 — 서버에 skipPromo:true로 전달.
const promoSkipped = ref(false);
// Type B(지정사) 수동 입력
const manualPromoInput = ref('');
const manualPromoChecking = ref(false);
const manualPromoResult = ref<{ valid: boolean; code?: string; discountRate?: number; reason?: string } | null>(null);
const manualPromoExpanded = ref(false);

async function fetchApplicablePromo() {
  appliedPromo.value = null;
  promoSkipped.value = false;
  try {
    const res = await companySettingsApi.getApplicablePromo(paymentType.value);
    appliedPromo.value = (res as any)?.data || null;
  } catch (err) {
    // 자동적용 조회 실패는 결제를 막지 않음(§5.2-2 자동적용 실패=무할인 진행과 동일 원칙) — 조용히 무시.
    console.warn('[Promo] applicable 조회 실패(무시)', err);
  }
}

async function validateManualPromoCode() {
  const code = manualPromoInput.value.trim();
  if (!code || manualPromoChecking.value) return;
  manualPromoChecking.value = true;
  try {
    const res: any = await companySettingsApi.validatePromoCode(code, paymentType.value, grandTotal.value);
    manualPromoResult.value = { valid: true, code: res?.code, discountRate: res?.discountRate };
  } catch (err: any) {
    manualPromoResult.value = { valid: false, reason: err?.response?.data?.reason || 'NOT_FOUND' };
  } finally {
    manualPromoChecking.value = false;
  }
}

function clearManualPromoCode() {
  manualPromoInput.value = '';
  manualPromoResult.value = null;
}

// 실제 적용될 할인율 — 수동 입력(Type B) 우선, 없으면 자동적용(Type A, 스킵 안 했을 때)
const effectivePromoRate = computed(() => {
  if (manualPromoResult.value?.valid) return manualPromoResult.value.discountRate || 0;
  if (appliedPromo.value && !promoSkipped.value) return appliedPromo.value.discountRate;
  return 0;
});
const promoDiscountAmount = computed(() => {
  if (effectivePromoRate.value <= 0) return 0;
  return Math.round(grandTotal.value * (effectivePromoRate.value / 100) * 100) / 100;
});
// 실제 청구 금액 — 서버가 동일 공식(§5.3)으로 재검증한다.
const finalTotal = computed(() => Math.round((grandTotal.value - promoDiscountAmount.value) * 100) / 100);
const appliedPromoCodeLabel = computed(() => manualPromoResult.value?.valid ? manualPromoResult.value.code : appliedPromo.value?.code);
function fmtPromoDeadline(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : `${d.getMonth() + 1}/${d.getDate()}`;
}

const costUnitLabel = computed(() => {
  const cs = currencySymbol.value;
  const qty = billingEntityCount.value;
  const label = isAddonMode.value
    ? t('subscription.planDialog.addonQty', { qty })
    : t('subscription.planDialog.entitiesQty', { qty });
  if (isMonthly.value) return `${cs}${selectedPlanPrice.value.toFixed(2)} \u00D7 ${label}`;
  // \uC99D\uC124\uC740 \uC794\uC5EC\uAE30\uAC04(\uC77C) \uBE44\uB840 \u2192 "\u00D7 N.NNyr (\uB0A8\uC740 \uC77C\uC218 remaining)"\uB85C \uD45C\uAE30\uD574 \uC65C \uAE08\uC561\uC774 \uC774\uB9CC\uD07C\uC778\uC9C0 \uBA85\uD655\uD788.
  if (isAddonMode.value) {
    const yrs = addonYears.value;
    const daysNote = addonRemainingDays.value != null
      ? ` ${t('subscription.planDialog.daysRemainingNote', { days: addonRemainingDays.value.toLocaleString() })}` : '';
    return `${cs}${selectedPlanPrice.value.toFixed(2)} \u00D7 ${label} \u00D7 ${yrs.toFixed(2)}yr${daysNote}`;
  }
  const years = effectiveYears.value;
  return `${cs}${selectedPlanPrice.value.toFixed(2)} \u00D7 ${label} \u00D7 ${years}yr`;
});

const hasItems = computed(() => {
  if (selectedPlan.value === 'trial') return false;
  if (isDecreaseMode.value) return true; // 감소는 결제 없이 진행 가능
  return billingEntityCount.value > 0;
});

// 관할(국가) 요금 미설정 등으로 단가가 0 이면 온라인 결제 불가 → Next 를 막고 관리자 문의(견적 요청) 안내.
// (백엔드도 amount<=0 을 400 으로 거부하므로, 프론트에서 미리 차단해 결제 실패 노출을 방지)
const pricingUnavailable = computed(() =>
  selectedPlan.value !== 'trial' && !isDecreaseMode.value && (selectedPlanPrice.value ?? 0) <= 0
);

/* ???? Step 2: Payment ???? */
const payCompanyName = computed(() => customerInfo.value?.name || '');
const payRegNumber = computed(() => customerInfo.value?.regNo || '');
const payBillingEmail = ref('');
const emailError = ref('');
// 청구 담당자명 — 인보이스 "Attn:" 으로 찍힌다. 비면 수신 담당자가 없는 인보이스가 나가므로 필수.
const payBillingContact = ref('');
const contactError = ref('');

function validateEmail(val: string) {
  if (!val || !val.trim()) {
    emailError.value = t('subscription.planDialog.emailRequired');
    return;
  }
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  emailError.value = re.test(val) ? '' : t('subscription.planDialog.emailInvalid');
}

function validateContact(val: string) {
  contactError.value = (val && val.trim()) ? '' : t('subscription.planDialog.contactRequired');
}

// Dialog open ??billingEmail prop???? ?????
watch(dialogVisible, (val) => {
  if (val && props.billingEmail) {
    payBillingEmail.value = props.billingEmail;
  }
  if (val && props.billingContactName) {
    payBillingContact.value = props.billingContactName;
  }
});

/* Airwallex Split Card Elements */
const airwallexError = ref('');
const airwallexReady = ref(false);
const airwallexLoading = ref(false);
let cardNumberElement: any = null;
let expiryElement: any = null;
let cvcElement: any = null;
let pendingIntentId = '';
let pendingClientSecret = '';
let pendingPaymentId = 0;

/* 저장된 카드(consent) — 있으면 재입력 없이 결제 */
const savedCard = ref<{ lastFour: string; brand: string; expMonth: number | null; expYear: number | null } | null>(null);
const useSavedCard = ref(false);        // true=저장카드로 결제, false=새 카드 입력
const savedCardHasConsent = ref(false); // 저장카드 결제(MIT/consent) 가능 여부 — consent 없으면 pay-saved-card 409
const loadingSavedCard = ref(false);
const savedCardExpired = computed(() => {
  const c = savedCard.value;
  if (!c || !c.expMonth || !c.expYear) return false;
  const exp = new Date(Date.UTC(c.expYear, c.expMonth, 1)); // 만료월 다음달 1일 = 만료 시점
  return exp <= new Date();
});
// 저장카드 "결제" 가능 = 카드번호 있음 + 미만료 + consent 있음(백엔드 pay-saved-card는 consent 필수).
// consent 없이 lastFour만 있으면(=direct 결제로 카드번호만 저장된 상태) 저장카드 결제 시 409 → 새 카드 입력으로 유도.
const hasValidSavedCard = computed(() => !!savedCard.value?.lastFour && !savedCardExpired.value && savedCardHasConsent.value);
const savedCardLabel = computed(() => {
  const c = savedCard.value; if (!c) return '';
  const exp = c.expMonth && c.expYear
    ? ` · ${t('subscription.planDialog.expShort', { month: String(c.expMonth).padStart(2, '0'), year: String(c.expYear).slice(-2) })}` : '';
  return `${c.brand || t('subscription.planDialog.cardFallback')} •••• ${c.lastFour}${exp}`;
});

async function fetchSavedCard() {
  loadingSavedCard.value = true;
  try {
    const res: any = await companySettingsApi.getBilling();
    const bi = (res?.data?.billingInfo) || res?.billingInfo || null;
    savedCard.value = bi?.cardLastFour
      ? { lastFour: bi.cardLastFour, brand: bi.cardBrand || 'Card', expMonth: bi.cardExpiryMonth, expYear: bi.cardExpiryYear }
      : null;
    savedCardHasConsent.value = !!bi?.hasConsent; // consent 있어야 저장카드 결제 가능
  } catch {
    savedCard.value = null;
    savedCardHasConsent.value = false;
  } finally {
    loadingSavedCard.value = false;
  }
}

// ???? ????? ?????????
let readyCount = 0;

// SDK ???? ????? ?????
let sdkLoaded = false;
let sdkLoadPromise: Promise<void> | null = null;

/** Dialog ????? ??SDK ???? ????? (Step 1 ????? ??????????? */
function preloadAirwallexSdk() {
  if (sdkLoaded || sdkLoadPromise) return;
  sdkLoadPromise = loadAirwallex({
    env: import.meta.env.VITE_AIRWALLEX_ENV || 'demo',
    origin: window.location.origin,
  }).then(() => {
    sdkLoaded = true;
  }).catch(() => {
    sdkLoadPromise = null; // ????? ???????????????????
  });
}

const splitStyle = {
  base: {
    color: '#1e1e1e',
    fontSize: '14px',
    fontFamily: '"Roboto", "Helvetica Neue", sans-serif',
    fontWeight: '400',
    '::placeholder': { color: '#9e9e9e' },
  } as Record<string, any>,
  invalid: { color: '#ff5252' },
  // 카드 검증(3DS) 챌린지가 뜰 때의 팝업 오버레이 크기. 기본값이 전체화면처럼 보여서
  // 일반적인 은행 인증창 크기로 지정(SDK의 PopUpStyle 옵션 — element.d.ts 참고).
  popupWidth: 450,
  popupHeight: 700,
};

// ready watchdog: iframe 3개(카드번호/만료일/CVC)가 일정 시간 내 'ready'를 안 쏘면(느린 네트워크,
// 일시적 CDN 지연 등) 자동으로 한 번 더 마운트를 재시도한다. 재시도까지 실패하면 무한 스피너 대신
// 명확한 에러 + 재시도 버튼을 보여준다(E2E에서도 동일 경로로 자동 복구되도록).
let readyWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
let watchdogRetried = false;
const READY_WATCHDOG_MS = 12_000;

function clearReadyWatchdog() {
  if (readyWatchdogTimer) {
    clearTimeout(readyWatchdogTimer);
    readyWatchdogTimer = null;
  }
}

function scheduleReadyWatchdog() {
  clearReadyWatchdog();
  readyWatchdogTimer = setTimeout(async () => {
    if (airwallexReady.value) return; // 이미 정상적으로 준비됨 — 아무 것도 안 함
    if (watchdogRetried) {
      // 재시도까지 실패 — 사용자에게 명확히 알리고 수동 재시도 유도
      airwallexLoading.value = false;
      airwallexError.value = t('subscription.planDialog.paymentFormLoadFailed');
      return;
    }
    watchdogRetried = true;
    await mountAirwallexFields();
  }, READY_WATCHDOG_MS);
}

/** SDK 로드(최초 1회) 후 Split Elements 마운트. initAirwallexElement()와 watchdog 재시도가 공유. */
async function mountAirwallexFields() {
  destroyAirwallexElement({ keepLoadingState: true });
  readyCount = 0;

  try {
    if (!sdkLoaded) {
      if (!sdkLoadPromise) {
        sdkLoadPromise = loadAirwallex({
          env: import.meta.env.VITE_AIRWALLEX_ENV || 'demo',
          origin: window.location.origin,
        }).then(() => { sdkLoaded = true; });
      }
      await sdkLoadPromise;
    }

    await nextTick();

    // Card Number
    cardNumberElement = createElement('cardNumber', { style: splitStyle, placeholder: '1234 1234 1234 1234' });
    cardNumberElement.mount('awx-card-number');
    cardNumberElement.on('ready', onFieldReady);
    cardNumberElement.on('error', onFieldError);
    cardNumberElement.on('change', onFieldChange);

    // Expiry
    expiryElement = createElement('expiry', { style: splitStyle, placeholder: 'MM / YY' });
    expiryElement.mount('awx-expiry');
    expiryElement.on('ready', onFieldReady);
    expiryElement.on('error', onFieldError);
    expiryElement.on('change', onFieldChange);

    // CVC
    cvcElement = createElement('cvc', { style: splitStyle, placeholder: 'CVC' });
    cvcElement.mount('awx-cvc');
    cvcElement.on('ready', onFieldReady);
    cvcElement.on('error', onFieldError);
    cvcElement.on('change', onFieldChange);

    scheduleReadyWatchdog();
  } catch (err: any) {
    airwallexError.value = err?.message || t('subscription.planDialog.paymentElementLoadFailed');
    airwallexLoading.value = false;
  }
}

async function initAirwallexElement() {
  // 재진입(Try Again/Back→Next) 시 기존 iframe이 남아 포커스/입력을 먹는 문제 방지 — 항상 정리 후 마운트
  destroyAirwallexElement();
  airwallexLoading.value = true;
  airwallexReady.value = false;
  airwallexError.value = '';
  watchdogRetried = false;
  await mountAirwallexFields();
}

function onFieldReady() {
  readyCount++;
  if (readyCount >= 3) {
    airwallexReady.value = true;
    airwallexLoading.value = false;
    clearReadyWatchdog();
  }
}
function onFieldError(evt: any) {
  airwallexError.value = evt?.error?.message || '';
}
function onFieldChange() {
  if (airwallexError.value) airwallexError.value = '';
}

async function onConfirmPayment() {
  // Billing E-Mail ????? ????
  validateEmail(payBillingEmail.value);
  validateContact(payBillingContact.value);
  if (emailError.value || contactError.value) return;

  // 저장 카드로 결제(재입력 없음)
  if (useSavedCard.value) {
    await payWithSavedCardFlow();
    return;
  }

  paymentStatus.value = 'processing';
  airwallexError.value = '';

  try {
    // Payment Intent????? ??? ?????????? (Back/Cancel ???????????? PENDING ???)
    const { intentId, clientSecret, paymentId } = await createPaymentIntent();
    pendingIntentId = intentId;
    pendingClientSecret = clientSecret;
    pendingPaymentId = paymentId;

    const savedIntentId = pendingIntentId;
    const savedPaymentId = pendingPaymentId;

    const confirmResult = await confirmPaymentIntent({
      element: cardNumberElement,
      intent_id: pendingIntentId,
      client_secret: pendingClientSecret,
      // 카드를 향후 저장카드 결제용으로 저장 → payment-verify가 consent 생성 + 저장카드 정보 갱신
      save_payment_method: true,
      // ⚠️ (버그 수정) 고객이 실제 CVC를 입력하는 이 순간에 payment_consent를 같이 요청해야
      // Airwallex가 "CVC 없이도 재청구 가능한" consent를 만든다. 이거 없이 결제 후 별도로
      // consent를 생성하면 Airwallex가 그 consent를 계속 "CVC 필요"로 취급해 저장카드 결제가
      // 항상 400(CVC is not present)으로 실패했다(실측 확인).
      payment_consent: {
        // merchant 유지: 서버에서 카드 재입력 없이 청구하려면 MIT 경로가 필요.
        next_triggered_by: 'merchant',
        // 'scheduled'는 고정 주기 청구(정기결제)를 뜻한다. 자동갱신을 폐지해 예약 청구가 없고
        // 저장카드는 사용자가 결제 버튼을 누를 때만 청구되므로 'unscheduled'가 실제와 맞다.
        merchant_trigger_reason: 'unscheduled',
      },
    });
    // Airwallex confirm 후 element 정리
    destroyAirwallexElement();

    try {
      const status = await verifyPaymentStatus(savedIntentId, savedPaymentId);

      step.value = 3;

      if (status === 'SUCCEEDED') {
        paymentStatus.value = 'success';
        paymentMessage.value = t('subscription.planDialog.paymentSuccessMessage', {
          currency: currencySymbol.value,
          amount: finalTotal.value.toLocaleString(),
          perMonth: isMonthly.value ? t('subscription.planDialog.perMonthSuffix') : '',
        });
        showSnackbar(t('subscription.planDialog.paymentCompletedSuccess'));
        emit('planUpdated', {
          plan: selectedPlan.value,
          duration: selectedDuration.value,
          addEntities: entityCount.value,
          amount: finalTotal.value,
          isMonthly: isMonthly.value
        });
      } else {
        paymentStatus.value = 'failed';
        paymentMessage.value = t('subscription.planDialog.paymentDeclinedDetail');
        showSnackbar(t('subscription.planDialog.paymentDeclined'), 'error');
      }
    } catch (err: any) {
      step.value = 3;
      paymentStatus.value = 'failed';
      paymentMessage.value = err?.message || t('subscription.planDialog.paymentVerificationFailed');
      showSnackbar(paymentMessage.value, 'error');
    }
  } catch (err: any) {
    // ??? ?????
    console.error('[Payment] onConfirmPayment error:', err);
    const msg = err?.message || t('subscription.planDialog.paymentFailedGeneric');
    paymentStatus.value = 'failed';
    paymentMessage.value = msg;
    step.value = 3;
    showSnackbar(msg, 'error');
    destroyAirwallexElement();

    // DB ???????FAILED?????????? (PENDING ??FAILED)
    if (pendingPaymentId) {
      verifyPaymentStatus(pendingIntentId, pendingPaymentId, 'FAILED').catch(() => {});
    }
  }
}

function destroyAirwallexElement(opts: { keepLoadingState?: boolean } = {}) {
  clearReadyWatchdog();
  [cardNumberElement, expiryElement, cvcElement].forEach(el => {
    if (el) { try { el.unmount(); } catch { /* ignore */ } }
  });
  cardNumberElement = null;
  expiryElement = null;
  cvcElement = null;
  readyCount = 0;
  airwallexReady.value = false;
  if (!opts.keepLoadingState) {
    airwallexLoading.value = false;
  }
  pendingIntentId = '';
  pendingClientSecret = '';
  pendingPaymentId = 0;
}

/* ???? Airwallex Payment Service (stub - connect to backend) ???? */

const billingPeriodMap: Record<string, string> = {
  monthly: 'monthly',
  '1y': 'yearly',
  '3y': '3yearly',
  '5y': '5yearly'
};

const billingPeriodValue = computed(() => {
  if (isCustomDuration.value) return `${customYears.value}yearly`;
  return billingPeriodMap[selectedDuration.value] || 'yearly';
});

/* 견적서 PDF 다운로드 — 현재 선택(기간/엔티티 수)을 반영 */
const quoteLoading = ref(false);
async function downloadQuotePdf() {
  quoteLoading.value = true;
  try {
    // 프로모션 코드(자동적용 Type A 우선, 수동 Type B가 유효하면 그 코드) — 견적서에도 결제와 동일 할인 반영.
    // 서버가 promoCode를 재검증(promoService)해 할인율을 도출하므로 프론트 표시율은 신뢰하지 않음.
    const quotePromoCode = manualPromoResult.value?.valid
      ? manualPromoResult.value.code
      : (appliedPromo.value && !promoSkipped.value ? appliedPromo.value.code : undefined);
    const res: any = await companySettingsApi.downloadQuote({
      billingPeriod: billingPeriodValue.value,
      entityCount: entityCount.value,
      paymentType: paymentType.value,
      promoCode: quotePromoCode || undefined,
    });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const disposition = (res.headers?.['content-disposition'] || res.headers?.get?.('content-disposition') || '') as string;
    const match = String(disposition).match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const fileName = match ? decodeURIComponent(match[1].replace(/"/g, '')) : 'Lettaga_Quotation.pdf';
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click();
    window.URL.revokeObjectURL(url); document.body.removeChild(a);
  } catch (err: any) {
    showSnackbar(err?.response?.data?.message || err?.message || t('subscription.planDialog.downloadQuoteFailed'), 'error');
  } finally {
    quoteLoading.value = false;
  }
}

const durationLabelMap = computed<Record<string, string>>(() => ({
  monthly: t('subscription.planDialog.durationMonthly'),
  '1y': t('subscription.planDialog.durationAnnual'),
  '3y': t('subscription.planDialog.duration3Year'),
  '5y': t('subscription.planDialog.duration5Year'),
}));

const durationLabel = computed(() => {
  if (isCustomDuration.value) return t('subscription.planDialog.durationNYear', { years: customYears.value });
  return durationLabelMap.value[selectedDuration.value] || t('subscription.planDialog.durationAnnual');
});

/** 결제 공통 payload (createPaymentIntent / payWithSavedCard 공유) */
function buildPaymentPayload() {
  const planDef = selectedPlanDef.value;
  const proratedNote = isProrated.value ? ` — prorated ${props.prorationRemainingDays}/${props.prorationTotalDays} days` : '';
  const planName = planDef?.name || selectedPlan.value;
  // 갱신은 영수증/메일에서 신규가입과 구분되도록 "Renewal" 명시(설명이 곧 Airwallex 인보이스 memo/line item).
  const description = isAddonMode.value
    ? `Entity Add-on +${entityDelta.value} (${durationLabel.value})${proratedNote}`
    : props.renewalMode
      ? `${planName} Plan Renewal - ${entityCount.value} Entities (${durationLabel.value})`
      : `${planName} Plan - ${entityCount.value} Entities (${durationLabel.value})`;
  // 프로모션 코드(§5.2-2) — 수동 입력(Type B) 우선, 없으면 자동적용(Type A) 코드, 둘 다 없고 자동적용을
  // 사용자가 껐으면(promoSkipped) skipPromo:true로 서버 자동탐색도 건너뛰게 함.
  const activePromoCode = manualPromoResult.value?.valid
    ? manualPromoResult.value.code
    : (appliedPromo.value && !promoSkipped.value ? appliedPromo.value.code : undefined);
  return {
    planCd: planDef?.planCd || '',
    billingPeriod: billingPeriodValue.value,
    entityQty: isAddonMode.value ? entityDelta.value : entityCount.value,
    paymentType: paymentType.value,
    amount: finalTotal.value,
    amountBeforeFloor: finalTotal.value,
    currency: planCurrency.value,
    unitPrice: selectedPlanPrice.value,
    productDescription: description,
    // 증설 프로레이션 정보 (감사/기록용)
    prorationDays: isProrated.value ? (props.prorationRemainingDays ?? undefined) : undefined,
    prorationTotalDays: isProrated.value ? (props.prorationTotalDays ?? undefined) : undefined,
    promoCode: activePromoCode || undefined,
    skipPromo: !activePromoCode && promoSkipped.value ? true : undefined,
    // 청구 연락처는 저장카드 결제에서도 보내야 한다 — 인보이스 Attn 의 원천이라
    // 빠지면 이메일로 폴백돼 담당자명이 문서에 안 찍힌다.
    billingEmail: payBillingEmail.value || undefined,
    billingContactName: payBillingContact.value || undefined,
  };
}

async function createPaymentIntent() {
  const res = await companySettingsApi.createPaymentIntent(buildPaymentPayload());
  return { intentId: res?.data?.intentId || '', clientSecret: res?.data?.clientSecret || '', paymentId: res?.data?.paymentId || 0 };
}


async function verifyPaymentStatus(_intentId: string, paymentId?: number, verifyStatus: string = 'SUCCEEDED') {
  if (paymentId) {
    const res = await companySettingsApi.verifyPayment(paymentId, _intentId, verifyStatus);
    return res?.data?.status === 'CONFIRMED' ? 'SUCCEEDED' : 'FAILED';
  }
  return verifyStatus;
}

/* ???? Deactivation Confirmation Dialog ???? */
const showDeactivateConfirm = ref(false);

/* ???? Actions ???? */
async function onNext() {
  // ????? ????????? ??limit???? ?????????? ??????????????
  if (entityBelowActive.value && !showDeactivateConfirm.value) {
    showDeactivateConfirm.value = true;
    return;
  }
  showDeactivateConfirm.value = false;

  // 감소 모드: 결제 스텝 없이 바로 적용 (환불 없음, 다음 갱신부터 감소 금액)
  if (isDecreaseMode.value) {
    await applyEntityDecrease();
    return;
  }

  step.value = 2;
  airwallexError.value = '';

  // 저장 카드 조회 → 유효하면 그 카드로 결제(재입력 없음), 없거나 만료면 새 카드 입력
  await fetchSavedCard();
  useSavedCard.value = hasValidSavedCard.value;

  // 프로모션 코드 자동적용 조회(§5.2-0) — 결제 금액/유형이 확정된 시점(Step 2 진입)에 조회.
  fetchApplicablePromo();

  try {
    await nextTick();
    // 저장카드 사용 시엔 Airwallex 카드입력 폼을 로드하지 않음(불필요)
    if (!useSavedCard.value) {
      await initAirwallexElement();
    }
  } catch (err: any) {
    airwallexError.value = err?.message || t('subscription.planDialog.paymentInitFailed');
  }
}

/** 새 카드 입력으로 전환 (저장카드 대신) — Airwallex 폼 로드 */
async function switchToNewCard() {
  useSavedCard.value = false;
  airwallexError.value = '';
  await nextTick();
  if (!airwallexReady.value) {
    try { await initAirwallexElement(); } catch (err: any) { airwallexError.value = err?.message || t('subscription.planDialog.paymentInitFailed'); }
  }
}

/** 저장카드로 결제 → payWithSavedCard + verifyPayment */
async function payWithSavedCardFlow() {
  paymentStatus.value = 'processing';
  airwallexError.value = '';
  try {
    const payload = buildPaymentPayload();
    const res: any = await companySettingsApi.payWithSavedCard(payload);
    const d = res?.data || res;
    const status = await verifyPaymentStatus(d.intentId, d.paymentId);
    step.value = 3;
    if (status === 'SUCCEEDED') {
      paymentStatus.value = 'success';
      paymentMessage.value = t('subscription.planDialog.paymentSuccessSavedCard', {
        currency: currencySymbol.value,
        amount: finalTotal.value.toLocaleString(),
        perMonth: isMonthly.value ? t('subscription.planDialog.perMonthSuffix') : '',
      });
      showSnackbar(t('subscription.planDialog.paymentCompletedSuccess'));
      emit('planUpdated', {
        plan: selectedPlan.value,
        duration: selectedDuration.value,
        addEntities: entityCount.value,
        amount: finalTotal.value,
        isMonthly: isMonthly.value,
      });
    } else {
      paymentStatus.value = 'failed';
      paymentMessage.value = t('subscription.planDialog.savedCardPaymentFailed');
    }
  } catch (err: any) {
    // 저장카드 없음/만료 → 새 카드 입력으로 전환
    const code = err?.response?.data?.code;
    if (code === 'NO_SAVED_CARD' || code === 'CARD_EXPIRED') {
      paymentStatus.value = 'idle';
      savedCard.value = code === 'CARD_EXPIRED' ? savedCard.value : null;
      await switchToNewCard();
      airwallexError.value = code === 'CARD_EXPIRED'
        ? t('subscription.planDialog.savedCardExpiredEnterNew')
        : t('subscription.planDialog.noSavedCardFound');
      return;
    }
    paymentStatus.value = 'failed';
    step.value = 3;
    paymentMessage.value = err?.response?.data?.message || err?.message || t('subscription.planDialog.paymentFailedGeneric');
  }
}

function onBack() {
  step.value = 1;
  airwallexError.value = '';
  destroyAirwallexElement();
}

/** 엔티티 감소 적용 — 결제 없음. 다음 갱신/자동청구부터 감소된 수량 기준 */
async function applyEntityDecrease() {
  try {
    const res: any = await companySettingsApi.decreaseEntityLimit(entityLimitInput.value);
    step.value = 3;
    if (res?.success) {
      paymentStatus.value = 'success';
      paymentMessage.value = t('subscription.planDialog.entityLimitDecreased', { limit: entityLimitInput.value.toLocaleString() });
      showSnackbar(t('subscription.planDialog.entityLimitDecreasedShort'));
      emit('planUpdated', {
        plan: selectedPlan.value,
        duration: selectedDuration.value,
        addEntities: entityLimitInput.value,
        amount: 0,
        isMonthly: isMonthly.value
      });
    } else {
      paymentStatus.value = 'failed';
      paymentMessage.value = res?.message || t('subscription.planDialog.decreaseEntityLimitFailed');
    }
  } catch (err: any) {
    step.value = 3;
    paymentStatus.value = 'failed';
    paymentMessage.value = err?.response?.data?.message || err?.message || t('subscription.planDialog.decreaseEntityLimitFailed');
  }
}


function onRetry() {
  step.value = 2;
  paymentStatus.value = 'idle';
  paymentMessage.value = '';

  // Retry: ???? ??? UI??????????? (Intent??Pay ??? ???????)
  (async () => {
    try {
      await nextTick();
      await initAirwallexElement();
    } catch (err: any) {
      airwallexError.value = err?.message || t('subscription.planDialog.paymentInitFailed');
    }
  })();
}

function onDone() {
  dialogVisible.value = false;
}

function onCancel() {
  if (paymentStatus.value === 'processing') return; // prevent closing during payment
  dialogVisible.value = false;
}
</script>

<template>
  <!-- retain-focus 비활성: Vuetify 포커스 트랩이 Airwallex 카드 iframe 포커스를 되뺏어 입력이 씹히는 문제 방지 (MyPresetPayDialog와 동일 패턴) -->
  <v-dialog v-model="dialogVisible" max-width="780" persistent scrollable :retain-focus="false">
    <v-card>
      <!-- ???? Header ???? -->
      <v-card-title class="d-flex justify-space-between align-center pa-5 pb-4">
        <span class="text-h5 font-weight-bold">{{ renewalMode ? t('subscription.planDialog.headerRenewal') : t('subscription.planDialog.headerUpdate') }}</span>
        <v-btn icon variant="text" size="small" :disabled="paymentStatus === 'processing'" @click="onCancel">
          <XIcon size="20" />
        </v-btn>
      </v-card-title>
      <v-divider />

      <!-- ???? 1. Current Plan Banner ???? -->
      <div class="bg-grey-lighten-4 px-5 py-3">
        <div class="d-flex align-center flex-wrap ga-2">
          <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.currentPlanLabel') }} :</span>
          <span class="text-body-2 font-weight-bold">{{ isTrial ? t('subscription.planDialog.trial') : (paidPlans.find(p => p.key === currentPlan)?.name ?? currentPlan) }}</span>
          <template v-if="!isTrial">
            <v-divider vertical class="mx-2" style="height: 16px" />
            <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.managedEntitiesLabel') }} :</span>
            <span class="text-body-2 font-weight-bold">{{ managedDisplay }}</span>
          </template>
          <template v-if="isTrial && trialDaysRemaining != null">
            <v-divider vertical class="mx-2" style="height: 16px" />
            <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.trialEndsIn') }}</span>
            <span class="text-body-2 font-weight-bold text-warning">{{ t('subscription.planDialog.daysCount', { count: trialDaysRemaining }) }}</span>
          </template>
        </div>
      </div>
      <v-divider />

      <v-card-text class="pa-5">
        <!-- ==================== Step 1: Plan Selection ==================== -->
        <template v-if="step === 1">
          <!-- Renewal Banner -->
          <v-alert v-if="renewalMode" type="info" variant="tonal" density="compact" class="mb-4">
            {{ t('subscription.planDialog.renewingBanner') }}
          </v-alert>

          <div class="d-flex justify-space-between align-center mb-3">
            <span class="text-subtitle-1 font-weight-medium">{{ renewalMode ? t('subscription.planDialog.plan') : t('subscription.planDialog.selectAPlan') }}</span>
            <v-chip size="small" label variant="tonal" color="primary">{{ planCurrency }} ({{ currencySymbol }})</v-chip>
          </div>

          <!-- Trial Option (hidden in renewal mode) -->
          <v-card
            v-if="!renewalMode"
            variant="outlined"
            class="plan-option mb-3 pa-3"
            :class="{ 'plan-option--selected': selectedPlan === 'trial', 'plan-option--disabled': trialDisabled }"
            @click="!trialDisabled && (selectedPlan = 'trial')"
          >
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center ga-3">
                <v-radio-group v-model="selectedPlan" hide-details class="ma-0 pa-0 flex-grow-0">
                  <v-radio value="trial" :disabled="trialDisabled" hide-details density="compact" />
                </v-radio-group>
                <span class="text-body-1 font-weight-medium" :class="{ 'text-medium-emphasis': trialDisabled }">{{ t('subscription.planDialog.trial') }}</span>
                <v-chip v-if="currentPlan === 'trial'" size="x-small" color="info" label>{{ t('subscription.planDialog.current') }}</v-chip>
              </div>
              <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.free30Days') }}</span>
            </div>
          </v-card>

          <!-- Paid Plans -->
          <v-card
            v-for="plan in paidPlans"
            :key="plan.key"
            variant="outlined"
            class="plan-option mb-3 pa-3"
            :class="{
              'plan-option--selected': selectedPlan === plan.key,
              'plan-option--disabled': renewalMode && plan.key !== currentPlan
            }"
            @click="!renewalMode && (selectedPlan = plan.key)"
          >
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center ga-3">
                <v-radio-group v-model="selectedPlan" hide-details class="ma-0 pa-0 flex-grow-0">
                  <v-radio :value="plan.key" :disabled="renewalMode && plan.key !== currentPlan" hide-details density="compact" />
                </v-radio-group>
                <span class="text-body-1 font-weight-medium">{{ plan.name }}</span>
                <v-chip v-if="currentPlan === plan.key" size="x-small" color="info" label>{{ t('subscription.planDialog.current') }}</v-chip>
                <v-chip v-if="renewalMode && plan.key === currentPlan" size="x-small" color="success" label>{{ t('subscription.planDialog.renewalChip') }}</v-chip>
              </div>
              <span class="text-body-2 font-weight-bold">
                {{ currencySymbol }} {{ (plan.prices[isCustomDuration ? '5y' : selectedDuration] ?? 0).toFixed(2) }} {{ t('subscription.planDialog.perEntity') }}{{ isMonthly ? ` ${t('subscription.planDialog.perMonthSuffix')}` : ` ${t('subscription.planDialog.perYearSuffix')}` }}
              </span>
            </div>
          </v-card>

          <!-- Billing Period -->
          <div class="mt-2 mb-1">
            <v-label class="font-weight-medium text-body-2">{{ t('subscription.planDialog.billingPeriod') }}</v-label>
          </div>
          <div class="d-flex align-center flex-wrap ga-2">
            <v-radio-group
              v-model="selectedDuration"
              inline
              hide-details
              density="compact"
              :disabled="selectedPlan === 'trial' || isPeriodLocked"
            >
              <v-radio :label="t('subscription.planDialog.durationMonthly')" value="monthly" />
              <v-radio :label="t('subscription.planDialog.oneYear')" value="1y" />
              <v-radio :label="threeYearLabel" value="3y" />
              <v-radio :label="fiveYearLabel" value="5y" />
              <v-radio :label="t('subscription.planDialog.custom')" value="custom" />
            </v-radio-group>
            <v-text-field
              v-if="isCustomDuration"
              v-model.number="customYears"
              type="number"
              :min="6"
              :max="10"
              :suffix="t('subscription.planDialog.years')"
              variant="outlined"
              density="compact"
              hide-details
              style="max-width: 120px"
              :disabled="selectedPlan === 'trial' || isPeriodLocked"
              @blur="onCustomYearsBlur"
            />
          </div>
          <v-alert v-if="isPeriodLocked" type="info" variant="tonal" density="compact" class="mt-2">
            <div class="text-caption">
              {{ t('subscription.planDialog.periodLockedForAddon') }}
            </div>
          </v-alert>

          <template v-if="selectedPlan !== 'trial'">
          <v-divider class="my-5" />

          <!-- Entity Limit -->
          <div class="d-flex justify-space-between align-center mb-3">
            <span class="text-subtitle-1 font-weight-medium">{{ t('subscription.planDialog.entityLimit') }}</span>
          </div>

          <!-- ????? ????? ?? -->
          <v-card variant="tonal" color="info" class="pa-3 mb-4" density="compact">
            <div class="d-flex align-center ga-4 text-body-2 flex-wrap">
              <span>{{ t('subscription.planDialog.activeEntitiesLabel') }} : <span class="font-weight-bold">{{ entityUsage.toLocaleString() }}</span></span>
              <template v-if="!isFirstSubscription && baseEntities > 0">
                <v-divider vertical class="mx-1" style="height: 14px" />
                <span>{{ t('subscription.planDialog.currentLimitLabel') }} : <span class="font-weight-bold">{{ baseEntities.toLocaleString() }}</span></span>
              </template>
            </div>
            <div class="text-caption mt-1 text-medium-emphasis">
              {{ t('subscription.planDialog.inactiveEntitiesNote') }}
            </div>
          </v-card>

          <!-- Entity Status guidance -->
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            <div class="text-caption">
              {{ t('subscription.planDialog.smallerPlanGuidance') }}
            </div>
          </v-alert>

          <!-- ???? ??? + ??????? -->
          <div class="d-flex align-center justify-end ga-2 mb-2 flex-wrap">
            <v-btn
              size="small"
              variant="tonal"
              color="warning"
              :disabled="entityLimitQuickDisabled || entityLimitInput - 10 < minRequired"
              @click="addEntityLimitQuick(-10)"
            >
              -10
            </v-btn>
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              :disabled="entityLimitQuickDisabled"
              @click="addEntityLimitQuick(10)"
            >
              +10
            </v-btn>
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              :disabled="entityLimitQuickDisabled"
              @click="addEntityLimitQuick(100)"
            >
              +100
            </v-btn>
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              :disabled="entityLimitQuickDisabled"
              @click="addEntityLimitQuick(1000)"
            >
              +1000
            </v-btn>
            <v-text-field
              v-model.number="entityLimitInput"
              :label="t('subscription.planDialog.entityLimit')"
              variant="outlined"
              density="compact"
              type="number"
              :step="entityStep"
              :min="minRequired"
              :disabled="selectedPlan === 'trial' || renewalMode"
              :readonly="true"
              hide-details
              style="max-width: 150px"
              @blur="onEntityLimitBlur"
            />
            <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.incrementsOf', { step: entityStep }) }}</span>
          </div>

          <!-- ???? ????: ??????????????? ????? -->
          <v-alert v-if="renewalMode" type="info" variant="tonal" density="compact" class="mb-4 mt-2">
            <div class="text-caption">
              {{ t('subscription.planDialog.entityCountFixedDuringRenewal') }}
            </div>
          </v-alert>

          <!-- Active ??? ?? -->
          <v-alert v-if="!renewalMode && entityBelowActive" type="warning" variant="tonal" density="compact" class="mb-4 mt-2">
            <div class="text-body-2 font-weight-medium mb-1">{{ t('subscription.planDialog.limitBelowActiveTitle') }}</div>
            <div class="text-caption">
              {{ t('subscription.planDialog.limitBelowActiveDetail', {
                active: entityUsage.toLocaleString(),
                limit: entityLimitInput.toLocaleString(),
                diff: (entityUsage - entityLimitInput).toLocaleString()
              }) }}
            </div>
            <div class="text-caption mt-2">
              {{ t('subscription.planDialog.avoidDeactivationGuidance') }}
            </div>
          </v-alert>

          <!-- ???? ??-->
          <v-card variant="outlined" class="pa-4 mt-3">
            <div class="d-flex align-center flex-wrap ga-2 mb-3">
              <!-- Included (?????) -->
              <span class="text-body-2">
                <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:#9e9e9e;vertical-align:middle;margin-right:4px;" />
                {{ t('subscription.planDialog.included') }} : <span class="font-weight-bold">{{ barIncluded }}</span>
              </span>
              <!-- ?? ??: ????? ????-->
              <template v-if="barExisting > 0">
                <span class="text-body-2 text-medium-emphasis mx-1">+</span>
                <span class="text-body-2">
                  <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:rgb(var(--v-theme-primary));vertical-align:middle;margin-right:4px;" />
                  {{ t('subscription.planDialog.currentBarLabel') }} : <span class="font-weight-bold text-primary">{{ barExisting.toLocaleString() }}</span>
                </span>
              </template>
              <!-- ???? ??????(?????) -->
              <template v-if="barNew > 0">
                <span class="text-body-2 text-medium-emphasis mx-1">+</span>
                <span class="text-body-2">
                  <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:rgb(var(--v-theme-success));vertical-align:middle;margin-right:4px;" />
                  {{ t('subscription.planDialog.newBarLabel') }} : <span class="font-weight-bold text-success">{{ barNew.toLocaleString() }}</span>
                </span>
              </template>
            </div>

            <!-- Segmented Bar -->
            <div style="height: 18px; border-radius: 9px; overflow: hidden; display: flex; background: #eee;">
              <div class="bg-grey" :style="{ width: includedBarPct + '%', minWidth: '4px' }" />
              <div v-if="barExisting > 0" class="bg-primary" :style="{ width: existingBarPct + '%', minWidth: '4px' }" />
              <div v-if="barNew > 0" class="bg-success" :style="{ width: newBarPct + '%', minWidth: '4px' }" />
            </div>

            <div class="d-flex justify-space-between align-center mt-2">
              <span class="text-caption text-medium-emphasis">{{ t('subscription.planDialog.min') }} : {{ includedBase }}</span>
              <span class="text-body-2">{{ t('subscription.planDialog.totalEntity') }} : <span class="font-weight-bold">{{ entityLimitInput.toLocaleString() }}</span></span>
            </div>
          </v-card>
          </template>

          <v-divider class="my-5" />

          <!-- Billing Summary -->
          <div class="d-flex justify-end">
            <v-card variant="outlined" class="pa-4" style="min-width: 300px; max-width: 380px; width: 100%">
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-subtitle-1 font-weight-bold">{{ t('subscription.planDialog.billingSummary') }}</span>
                <v-chip v-if="renewalMode" size="small" color="success" label>{{ t('subscription.planDialog.renewalChip') }}</v-chip>
                <v-chip v-else-if="isAddonMode" size="small" color="primary" label>{{ t('subscription.planDialog.entityAddonChip') }}</v-chip>
              </div>
              <v-divider class="mb-3" />
              <v-alert v-if="pricingUnavailable" type="warning" variant="tonal" density="compact" class="mb-3 text-caption">
                {{ t('subscription.planDialog.pricingUnavailable') }}
              </v-alert>
              <template v-if="hasItems && !isDecreaseMode">
                <div class="mb-3">
                  <div class="d-flex justify-space-between">
                    <span class="text-body-2 text-medium-emphasis">
                      <template v-if="isAddonMode">
                        {{ t('subscription.planDialog.entityAddonLine', { count: entityDelta.toLocaleString() }) }}
                      </template>
                      <template v-else>
                        {{ selectedPlanDef?.name || t('subscription.planDialog.plan') }} {{ t('subscription.planDialog.entitiesCountParen', { count: entityCount.toLocaleString() }) }} :
                      </template>
                    </span>
                    <span class="text-body-2 font-weight-medium">{{ currencySymbol }} {{ totalCost.toLocaleString() }}</span>
                  </div>
                  <div class="text-caption text-medium-emphasis mt-1">{{ costUnitLabel }}</div>
                </div>
              </template>
              <div v-else-if="isDecreaseMode" class="py-2">
                <div class="d-flex justify-space-between">
                  <span class="text-body-2">{{ t('subscription.planDialog.entityDecreaseLine', { count: entityDelta.toLocaleString() }) }}</span>
                  <span class="text-body-2 font-weight-medium">{{ currencySymbol }} 0</span>
                </div>
                <div class="text-caption text-medium-emphasis mt-1">
                  {{ t('subscription.planDialog.decreaseNoChargeNote') }}
                </div>
              </div>
              <div v-else class="text-center py-3">
                <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.noItemsSelected') }}</span>
              </div>
              <template v-if="taxAmount > 0">
                <div class="d-flex justify-space-between mt-1">
                  <span class="text-body-2 text-medium-emphasis">
                    {{ taxLabel }} ({{ (taxRate * 100).toFixed(1) }}%) :
                  </span>
                  <span class="text-body-2 font-weight-medium">{{ currencySymbol }} {{ taxAmount.toLocaleString() }}</span>
                </div>
              </template>
              <!-- 프로모션 자동적용(Type A) — 이전 화면·견적서에도 결제화면과 동일하게 할인 반영.
                   만료/사용불가 프로모는 fetchApplicablePromo가 반환하지 않아 자동 미표시. -->
              <template v-if="hasItems && !isDecreaseMode && effectivePromoRate > 0">
                <div class="d-flex justify-space-between align-center mt-2">
                  <v-chip size="small" color="success" variant="tonal">
                    {{ appliedPromoCodeLabel }} · {{ effectivePromoRate }}%
                    <span v-if="appliedPromo?.deadline" class="ml-1">· {{ t('subscription.planDialog.promoUntil', { date: fmtPromoDeadline(appliedPromo.deadline) }) }}</span>
                  </v-chip>
                </div>
                <div class="d-flex justify-space-between mt-2">
                  <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.promoDiscountLabel', { code: appliedPromoCodeLabel, rate: effectivePromoRate }) }} :</span>
                  <span class="text-body-2 font-weight-medium text-success">- {{ currencySymbol }} {{ promoDiscountAmount.toLocaleString() }}</span>
                </div>
              </template>
              <v-divider class="my-2" />
              <div class="d-flex justify-space-between">
                <span class="text-subtitle-1 font-weight-bold">{{ t('subscription.planDialog.total') }} :</span>
                <span class="text-subtitle-1 font-weight-bold" :class="finalTotal > 0 ? 'text-primary' : ''">
                  {{ currencySymbol }} {{ finalTotal.toLocaleString() }}{{ isMonthly ? ` ${t('subscription.planDialog.perMonthSuffix')}` : '' }}
                </span>
              </div>
              <div v-if="!isMonthly && hasItems && !isDecreaseMode" class="text-right mt-1">
                <span class="text-caption text-medium-emphasis">( {{ t('subscription.planDialog.yearsUpfront', { years: effectiveYears }) }} )</span>
              </div>
              <!-- 갱신 후 만료일 미리보기 — 결제 전에 "언제까지로 연장되는지" 명확히 -->
              <div v-if="renewalMode && renewalNewExpiry" class="mt-3 pt-3" style="border-top: 1px dashed rgba(0,0,0,0.12)">
                <div class="d-flex justify-space-between">
                  <span class="text-caption text-medium-emphasis">{{ t('subscription.planDialog.currentExpiry') }}</span>
                  <span class="text-caption">{{ currentExpiryLabel }}</span>
                </div>
                <div class="d-flex justify-space-between mt-1">
                  <span class="text-caption text-medium-emphasis">{{ t('subscription.planDialog.newExpiryAfterRenewal') }}</span>
                  <span class="text-caption font-weight-bold text-primary">{{ newExpiryLabel }}</span>
                </div>
                <div class="text-caption text-medium-emphasis mt-1" style="font-size: 11px;">
                  <template v-if="renewalStacksFromExpiry">
                    {{ t('subscription.planDialog.renewalStacksNote', { duration: isMonthly ? t('subscription.planDialog.oneMonth') : t('subscription.planDialog.yearsCount', { count: effectiveYears }) }) }}
                  </template>
                  <template v-else>
                    {{ t('subscription.planDialog.renewalExpiredNote') }}
                  </template>
                </div>
              </div>
              <!-- 증설(Entity Add-on)은 즉석 결제라 승인용 견적서가 불필요 — 신규가입/갱신에서만 노출 -->
              <div v-if="hasItems && !isDecreaseMode && !isAddonMode" class="mt-3">
                <v-btn variant="tonal" size="small" color="primary" block :loading="quoteLoading" @click="downloadQuotePdf">
                  <v-icon size="16" class="mr-1">mdi-file-download-outline</v-icon>
                  {{ t('subscription.planDialog.downloadQuotePdf') }}
                </v-btn>
              </div>
            </v-card>
          </div>
        </template>

        <!-- ==================== Step 2: Payment ==================== -->
        <template v-if="step === 2">
          <!-- Step Indicator -->
          <div class="d-flex align-center ga-3 mb-5">
            <div class="d-flex align-center ga-2">
              <v-avatar color="primary" size="28">
                <CheckIcon size="16" class="text-white" />
              </v-avatar>
              <span class="text-body-2 font-weight-medium">{{ t('subscription.planDialog.stepSelectPlan') }}</span>
            </div>
            <v-divider style="max-width: 50px" />
            <div class="d-flex align-center ga-2">
              <v-avatar color="primary" size="28">
                <span class="text-white text-caption">2</span>
              </v-avatar>
              <span class="text-body-2 font-weight-medium">{{ t('subscription.planDialog.stepPayment') }}</span>
            </div>
            <v-divider style="max-width: 50px" />
            <div class="d-flex align-center ga-2">
              <v-avatar color="grey-lighten-1" size="28">
                <span class="text-white text-caption">3</span>
              </v-avatar>
              <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.stepResult') }}</span>
            </div>
          </div>

          <div class="upd-checkout">
          <div class="upd-checkout-main">
          <!-- Payment Information -->
          <div class="text-subtitle-1 font-weight-medium mb-3">{{ t('subscription.planDialog.paymentInformation') }}</div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-label class="mb-1 font-weight-medium text-body-2">{{ t('subscription.planDialog.companyName') }}</v-label>
              <v-text-field :model-value="payCompanyName" color="primary" variant="outlined" density="compact" hide-details readonly />
            </v-col>
            <v-col cols="12" md="6">
              <v-label class="mb-1 font-weight-medium text-body-2">{{ t('subscription.planDialog.registrationNumber') }}</v-label>
              <v-text-field :model-value="payRegNumber" color="primary" variant="outlined" density="compact" hide-details readonly />
            </v-col>
            <v-col cols="12" md="6" class="mt-2">
              <v-label class="mb-1 font-weight-medium text-body-2">{{ t('subscription.planDialog.billingContact') }}</v-label>
              <v-text-field
                v-model="payBillingContact"
                color="primary"
                variant="outlined"
                density="compact"
                :placeholder="t('subscription.planDialog.billingContactPlaceholder')"
                :error-messages="contactError"
                :hint="t('subscription.planDialog.billingContactHint')"
                :persistent-hint="!contactError"
                @blur="validateContact(payBillingContact)"
                @update:model-value="(v: string) => validateContact(v)"
              />
            </v-col>
            <v-col cols="12" md="6" class="mt-2">
              <v-label class="mb-1 font-weight-medium text-body-2">{{ t('subscription.planDialog.billingEmail') }}</v-label>
              <v-text-field
                v-model="payBillingEmail"
                color="primary"
                variant="outlined"
                density="compact"
                type="email"
                placeholder="billing@company.com"
                :error-messages="emailError"
                :hide-details="!emailError"
                @blur="validateEmail(payBillingEmail)"
                @update:model-value="(v: string) => validateEmail(v)"
              />
            </v-col>
          </v-row>

          <!-- Payment method -->
          <div class="text-body-2 font-weight-medium mt-5 mb-2">
            <v-icon size="18" color="primary" class="mr-1">mdi-credit-card-outline</v-icon>
            {{ t('subscription.planDialog.paymentMethod') }}
          </div>

          <!-- 저장된 카드 사용 -->
          <div v-if="useSavedCard && savedCard" class="mb-2">
            <v-card variant="outlined" class="pa-3 d-flex align-center justify-space-between" style="border-color:rgb(var(--v-theme-primary));">
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-credit-card-check-outline</v-icon>
                <div>
                  <div class="text-body-2 font-weight-medium">{{ savedCardLabel }}</div>
                  <div class="text-caption text-medium-emphasis">{{ t('subscription.planDialog.savedCardNoReentry') }}</div>
                </div>
              </div>
              <v-btn variant="text" size="small" color="primary" @click="switchToNewCard">{{ t('subscription.planDialog.useDifferentCard') }}</v-btn>
            </v-card>
          </div>

          <!-- 저장카드 만료 안내 -->
          <v-alert v-if="!useSavedCard && savedCard && savedCardExpired" type="warning" variant="tonal" density="compact" class="mb-2 text-body-2">
            {{ t('subscription.planDialog.savedCardExpiredAlert', { card: savedCardLabel }) }}
          </v-alert>

          <!-- 새 카드 입력 (저장카드 미사용 시) -->
          <template v-if="!useSavedCard">
          <div class="d-flex align-center justify-space-between mt-2 mb-2">
            <div class="text-body-2 font-weight-medium">
              {{ t('subscription.planDialog.creditCard') }}
            </div>
            <v-btn v-if="hasValidSavedCard" variant="text" size="small" color="primary" @click="useSavedCard = true">{{ t('subscription.planDialog.useSavedCard') }}</v-btn>
          </div>

          <div v-if="airwallexLoading" class="d-flex align-center justify-center py-5">
            <v-progress-circular indeterminate size="24" width="2" color="primary" class="mr-3" />
            <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.loadingPaymentForm') }}</span>
          </div>

          <v-row dense :class="{ 'd-none': !airwallexReady }">
            <v-col cols="12">
              <div class="d-flex align-center justify-space-between mb-1">
                <v-label class="font-weight-medium text-body-2">{{ t('subscription.planDialog.cardNumber') }}</v-label>
                <div class="d-flex align-center ga-1">
                  <img :src="visaIcon" alt="Visa" height="24" />
                  <img :src="mastercardIcon" alt="Mastercard" height="24" />
                  <img :src="amexIcon" alt="AMEX" height="24" />
                  <img :src="jcbIcon" alt="JCB" height="24" />
                  <img :src="unionpayIcon" alt="UnionPay" height="24" />
                </div>
              </div>
              <div class="awx-field-wrapper" id="awx-card-number" />
            </v-col>
            <v-col cols="6">
              <v-label class="mb-1 font-weight-medium text-body-2">{{ t('subscription.planDialog.expiry') }}</v-label>
              <div class="awx-field-wrapper" id="awx-expiry" />
            </v-col>
            <v-col cols="6">
              <v-label class="mb-1 font-weight-medium text-body-2">{{ t('subscription.planDialog.cvc') }}</v-label>
              <div class="awx-field-wrapper" id="awx-cvc" />
            </v-col>
          </v-row>

          <div v-if="airwallexError" class="text-caption text-error mt-1 px-1">{{ airwallexError }}</div>
          </template>

          <!-- Payment Notice -->
          <v-alert variant="tonal" density="compact" class="text-body-2 mt-3 payment-notice-alert" color="grey-darken-3" icon="mdi-information-outline">
            <div>{{ t('subscription.planDialog.chargeImmediatelyNote') }}</div>
            <div>{{ t('subscription.planDialog.processedByAirwallex') }}</div>
            <div>{{ t('subscription.planDialog.activationAfterConfirmation') }}</div>
          </v-alert>
          </div><!-- /upd-checkout-main -->

          <!-- Billing Summary -->
          <div class="upd-checkout-side">
            <v-card variant="outlined" class="pa-4" style="width: 100%">
              <div class="text-subtitle-1 font-weight-bold mb-2">{{ t('subscription.planDialog.billingSummary') }}</div>
              <v-divider class="mb-3" />
              <template v-if="hasItems && !isDecreaseMode">
                <div class="mb-3">
                  <div class="d-flex justify-space-between">
                    <span class="text-body-2 text-medium-emphasis">{{ selectedPlanDef?.name || t('subscription.planDialog.plan') }} {{ t('subscription.planDialog.entitiesCountParen', { count: entityCount }) }} :</span>
                    <span class="text-body-2 font-weight-medium">{{ currencySymbol }} {{ totalCost.toLocaleString() }}</span>
                  </div>
                  <div class="text-caption text-medium-emphasis mt-1">{{ costUnitLabel }}</div>
                </div>
              </template>
              <template v-if="taxAmount > 0">
                <div class="d-flex justify-space-between mt-1">
                  <span class="text-body-2 text-medium-emphasis">
                    {{ taxLabel }} ({{ (taxRate * 100).toFixed(1) }}%) :
                  </span>
                  <span class="text-body-2 font-weight-medium">{{ currencySymbol }} {{ taxAmount.toLocaleString() }}</span>
                </div>
              </template>

              <!-- 프로모션 코드(§5.2) -->
              <template v-if="hasItems && !isDecreaseMode">
                <!-- 자동적용(Type A) 칩 -->
                <div v-if="appliedPromo && !promoSkipped && !manualPromoResult?.valid" class="d-flex justify-space-between align-center mt-2">
                  <v-chip size="small" color="success" variant="tonal">
                    {{ appliedPromo.code }} · {{ appliedPromo.discountRate }}%
                    <span v-if="appliedPromo.deadline" class="ml-1">· {{ t('subscription.planDialog.promoUntil', { date: fmtPromoDeadline(appliedPromo.deadline) }) }}</span>
                  </v-chip>
                  <v-btn size="x-small" variant="text" @click="promoSkipped = true">{{ t('subscription.planDialog.promoRemove') }}</v-btn>
                </div>

                <!-- 수동 입력(Type B) — 자동적용이 없거나 사용자가 껐을 때만 노출 -->
                <div v-else-if="!manualPromoResult?.valid" class="mt-2">
                  <a href="#" class="text-caption promo-toggle-link" @click.prevent="manualPromoExpanded = !manualPromoExpanded">
                    {{ t('subscription.planDialog.promoHaveCode') }}
                  </a>
                  <div v-if="manualPromoExpanded" class="d-flex align-center mt-2" style="gap: 8px;">
                    <v-text-field
                      v-model="manualPromoInput"
                      :label="t('subscription.planDialog.promoInputLabel')"
                      variant="outlined" density="compact" hide-details
                      :disabled="manualPromoChecking"
                      @keydown.enter.prevent="validateManualPromoCode"
                    />
                    <v-btn size="small" color="primary" variant="outlined" :loading="manualPromoChecking" :disabled="!manualPromoInput.trim()" @click="validateManualPromoCode">
                      {{ t('subscription.planDialog.promoApply') }}
                    </v-btn>
                  </div>
                  <div v-if="manualPromoResult && !manualPromoResult.valid" class="text-caption text-error mt-1">
                    {{ t(`subscription.planDialog.promoReason${manualPromoResult.reason}`) || t('subscription.planDialog.promoReasonNOT_FOUND') }}
                  </div>
                </div>

                <!-- 수동 입력 성공 -->
                <div v-else class="d-flex justify-space-between align-center mt-2">
                  <v-chip size="small" color="success" variant="tonal">{{ manualPromoResult.code }} · {{ manualPromoResult.discountRate }}%</v-chip>
                  <v-btn size="x-small" variant="text" @click="clearManualPromoCode">{{ t('subscription.planDialog.promoRemove') }}</v-btn>
                </div>

                <div v-if="effectivePromoRate > 0" class="d-flex justify-space-between mt-2">
                  <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.promoDiscountLabel', { code: appliedPromoCodeLabel, rate: effectivePromoRate }) }} :</span>
                  <span class="text-body-2 font-weight-medium text-success">- {{ currencySymbol }} {{ promoDiscountAmount.toLocaleString() }}</span>
                </div>
              </template>

              <v-divider class="my-2" />
              <div class="d-flex justify-space-between">
                <span class="text-subtitle-1 font-weight-bold">{{ t('subscription.planDialog.total') }} :</span>
                <span class="text-subtitle-1 font-weight-bold" :class="finalTotal > 0 ? 'text-primary' : ''">
                  {{ currencySymbol }} {{ finalTotal.toLocaleString() }}{{ isMonthly ? ` ${t('subscription.planDialog.perMonthSuffix')}` : '' }}
                </span>
              </div>
              <div v-if="!isMonthly && hasItems" class="text-right mt-1">
                <span class="text-caption text-medium-emphasis">( {{ t('subscription.planDialog.yearsUpfront', { years: effectiveYears }) }} )</span>
              </div>
              <!-- 갱신 후 만료일 미리보기(결제 직전 재확인) -->
              <div v-if="renewalMode && renewalNewExpiry" class="mt-3 pt-3" style="border-top: 1px dashed rgba(0,0,0,0.12)">
                <div class="d-flex justify-space-between">
                  <span class="text-caption text-medium-emphasis">{{ t('subscription.planDialog.currentExpiry') }}</span>
                  <span class="text-caption">{{ currentExpiryLabel }}</span>
                </div>
                <div class="d-flex justify-space-between mt-1">
                  <span class="text-caption text-medium-emphasis">{{ t('subscription.planDialog.newExpiryAfterRenewal') }}</span>
                  <span class="text-caption font-weight-bold text-primary">{{ newExpiryLabel }}</span>
                </div>
              </div>
            </v-card>
          </div>
          </div><!-- /upd-checkout -->
        </template>

        <!-- ==================== Step 3: Payment Result ==================== -->
        <template v-if="step === 3">
          <!-- Step Indicator -->
          <div class="d-flex align-center ga-3 mb-5">
            <div class="d-flex align-center ga-2">
              <v-avatar color="primary" size="28">
                <CheckIcon size="16" class="text-white" />
              </v-avatar>
              <span class="text-body-2 font-weight-medium">{{ t('subscription.planDialog.stepSelectPlan') }}</span>
            </div>
            <v-divider style="max-width: 50px" />
            <div class="d-flex align-center ga-2">
              <v-avatar color="primary" size="28">
                <CheckIcon size="16" class="text-white" />
              </v-avatar>
              <span class="text-body-2 font-weight-medium">{{ t('subscription.planDialog.stepPayment') }}</span>
            </div>
            <v-divider style="max-width: 50px" />
            <div class="d-flex align-center ga-2">
              <v-avatar :color="paymentStatus === 'success' ? 'success' : paymentStatus === 'failed' ? 'error' : 'primary'" size="28">
                <CheckIcon v-if="paymentStatus === 'success'" size="16" class="text-white" />
                <AlertCircleIcon v-else-if="paymentStatus === 'failed'" size="16" class="text-white" />
                <span v-else class="text-white text-caption">3</span>
              </v-avatar>
              <span class="text-body-2 font-weight-medium">{{ t('subscription.planDialog.stepResult') }}</span>
            </div>
          </div>

          <!-- Processing -->
          <div v-if="paymentStatus === 'processing'" class="d-flex flex-column align-center py-10">
            <v-progress-circular indeterminate color="primary" size="64" width="4" class="mb-4" />
            <div class="text-h5 font-weight-bold mb-2">{{ t('subscription.planDialog.processingPayment') }}</div>
            <div class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.doNotCloseWindow') }}</div>
          </div>

          <!-- Success -->
          <div v-if="paymentStatus === 'success'" class="d-flex flex-column align-center py-10">
            <div class="upd-result-icon upd-result-icon--ok mb-4">
              <v-avatar color="success" size="72">
                <CircleCheckIcon size="40" class="text-white" />
              </v-avatar>
            </div>
            <div class="text-h5 font-weight-bold mb-2">{{ isDecreaseMode ? t('subscription.planDialog.entityLimitUpdated') : t('subscription.planDialog.paymentSuccessful') }}</div>
            <div class="text-body-2 text-medium-emphasis text-center" style="max-width: 400px">
              {{ paymentMessage }}
            </div>
            <v-card variant="outlined" rounded="lg" class="pa-5 mt-6" style="min-width: 320px; max-width: 360px">
              <div class="d-flex justify-space-between mb-2">
                <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.plan') }} :</span>
                <span class="text-body-2 font-weight-medium">{{ selectedPlanDef?.name ?? selectedPlan }}</span>
              </div>
              <div class="d-flex justify-space-between mb-2">
                <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.billingLabel') }} :</span>
                <span class="text-body-2 font-weight-medium">{{ isMonthly ? t('subscription.planDialog.durationMonthly') : t('subscription.planDialog.yearsUpfront', { years: effectiveYears }) }}</span>
              </div>
              <div class="d-flex justify-space-between mb-2">
                <span class="text-body-2 text-medium-emphasis">{{ t('subscription.planDialog.entitiesLabel') }} :</span>
                <span class="text-body-2 font-weight-medium">{{ entityCount }}</span>
              </div>
              <v-divider class="my-3" />
              <div class="d-flex justify-space-between align-center">
                <span class="text-body-2 font-weight-bold">{{ t('subscription.planDialog.amountCharged') }} :</span>
                <span class="text-subtitle-1 font-weight-bold text-primary">
                  {{ currencySymbol }} {{ (isDecreaseMode ? 0 : finalTotal).toLocaleString() }}{{ isMonthly && !isDecreaseMode ? ` ${t('subscription.planDialog.perMonthSuffix')}` : '' }}
                </span>
              </div>
            </v-card>
          </div>

          <!-- Failed -->
          <div v-if="paymentStatus === 'failed'" class="d-flex flex-column align-center py-10">
            <div class="upd-result-icon upd-result-icon--err mb-4">
              <v-avatar color="error" size="72">
                <AlertCircleIcon size="40" class="text-white" />
              </v-avatar>
            </div>
            <div class="text-h5 font-weight-bold mb-2">{{ t('subscription.planDialog.paymentFailedTitle') }}</div>
            <div class="text-body-2 text-medium-emphasis text-center" style="max-width: 400px">
              {{ paymentMessage }}
            </div>
          </div>
        </template>
      </v-card-text>

      <!-- ???? Footer ???? -->
      <v-divider />
      <v-card-actions class="pa-5">
        <!-- Step 1 -->
        <template v-if="step === 1">
          <v-btn variant="outlined" rounded="pill" @click="onCancel">{{ t('subscription.planDialog.cancel') }}</v-btn>
          <v-spacer />
          <v-btn variant="flat" :color="isDecreaseMode ? 'warning' : 'primary'" rounded="pill" :disabled="!hasItems || pricingUnavailable" @click="onNext">{{ isDecreaseMode ? t('subscription.planDialog.applyDecrease', { count: entityDelta.toLocaleString() }) : t('subscription.planDialog.next') }}</v-btn>
        </template>

        <!-- Step 2 -->
        <template v-if="step === 2">
          <v-btn variant="outlined" rounded="pill" :disabled="paymentStatus === 'processing'" @click="onCancel">{{ t('subscription.planDialog.cancel') }}</v-btn>
          <v-spacer />
          <v-btn variant="outlined" color="primary" rounded="pill" :disabled="paymentStatus === 'processing'" @click="onBack">{{ t('subscription.planDialog.back') }}</v-btn>
          <v-btn
            variant="flat"
            color="primary"
            rounded="pill"
            class="ml-3"
            :disabled="(!useSavedCard && !airwallexReady) || paymentStatus === 'processing' || !!emailError || !payBillingEmail.trim() || !!contactError || !payBillingContact.trim()"
            :loading="paymentStatus === 'processing'"
            @click="onConfirmPayment"
          >
            {{ useSavedCard ? t('subscription.planDialog.payWithSavedCard') : t('subscription.planDialog.pay') }} {{ currencySymbol }} {{ finalTotal.toLocaleString() }}
          </v-btn>
        </template>

        <!-- Step 3 -->
        <template v-if="step === 3">
          <template v-if="paymentStatus === 'success'">
            <v-spacer />
            <v-btn variant="flat" color="primary" rounded="pill" @click="onDone">{{ t('subscription.planDialog.done') }}</v-btn>
          </template>
          <template v-if="paymentStatus === 'failed'">
            <v-btn variant="outlined" rounded="pill" @click="onCancel">{{ t('subscription.planDialog.cancel') }}</v-btn>
            <v-spacer />
            <v-btn variant="flat" color="primary" rounded="pill" @click="onRetry">{{ t('subscription.planDialog.tryAgain') }}</v-btn>
          </template>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Entity Deactivation Confirmation Dialog -->
  <v-dialog v-model="showDeactivateConfirm" max-width="480" persistent>
    <v-card>
      <v-card-title class="d-flex align-center ga-2 pa-5 pb-3">
        <v-icon color="warning" size="24">mdi-alert-circle-outline</v-icon>
        <span class="text-h6">{{ t('subscription.planDialog.confirmDeactivationTitle') }}</span>
      </v-card-title>
      <v-card-text class="pa-5 pt-2">
        <div class="text-body-2 mb-3">
          {{ t('subscription.planDialog.confirmDeactivationDetail', {
            limit: entityLimitInput.toLocaleString(),
            active: entityUsage.toLocaleString()
          }) }}
        </div>
        <v-alert type="warning" variant="tonal" density="compact">
          <div class="text-body-2">
            {{ t('subscription.planDialog.confirmDeactivationWarning', { diff: (entityUsage - entityLimitInput).toLocaleString() }) }}
          </div>
        </v-alert>
      </v-card-text>
      <v-card-actions class="pa-5 pt-0">
        <v-btn variant="outlined" rounded="pill" @click="showDeactivateConfirm = false">{{ t('subscription.planDialog.cancel') }}</v-btn>
        <v-spacer />
        <v-btn variant="flat" color="warning" rounded="pill" @click="onNext">{{ t('subscription.planDialog.continue') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Snackbar -->
  <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" location="top right">
    {{ snackbar.message }}
  </v-snackbar>

</template>

<style scoped>
/* Step 2 checkout — 2-column (main + order summary). Layout only; inner blocks unchanged. */
.upd-checkout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 24px;
  align-items: start;
}
@media (max-width: 720px) {
  .upd-checkout {
    grid-template-columns: 1fr;
  }
}
/* Step 3 result — soft halo behind the status icon (display only) */
.upd-result-icon {
  display: grid;
  place-items: center;
  width: 104px;
  height: 104px;
  border-radius: 50%;
}
.upd-result-icon--ok {
  background: rgba(var(--v-theme-success), 0.12);
}
.upd-result-icon--err {
  background: rgba(var(--v-theme-error), 0.12);
}
.promo-toggle-link {
  color: rgba(var(--v-theme-on-surface), 0.75);
  font-weight: 500;
  text-decoration: none;
}
.promo-toggle-link:hover {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}
.plan-option {
  cursor: pointer;
  border: 2px solid rgba(0, 0, 0, 0.12);
  transition: border-color 0.2s;
}
.plan-option:hover:not(.plan-option--disabled) {
  border-color: rgba(var(--v-theme-primary), 0.4);
}
.plan-option--selected {
  border-color: rgb(var(--v-theme-primary)) !important;
}
.plan-option--disabled {
  cursor: default;
  opacity: 0.5;
}

/* Airwallex split field ??Vuetify outlined style match */
.awx-field-wrapper {
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 4px;
  padding: 10px 14px;
  min-height: 40px;
  transition: border-color 0.2s ease;
  background: #fff;
}
.awx-field-wrapper:hover {
  border-color: rgba(0, 0, 0, 0.87);
}
.awx-field-wrapper:focus-within {
  border-color: rgb(var(--v-theme-primary));
  border-width: 2px;
  padding: 9px 13px;
}
</style>

